import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AnyAction } from "redux";
import type { ThunkDispatch } from "redux-thunk";
import { createSelector } from "reselect";
import { sendConversationMessage } from "../store/actions/genericActions";
import type { ChatReducer } from "../store/chat.reducer";
import type { AppState } from "../store/definitions";
import type { SessionReducer } from "../store/session.reducer";

const useInQueueMessagingSelector = createSelector(
    (state: AppState) => state,
    (state) => ({
        inQueueMessagingConfig: state.config.inQueueMessaging,
        escalationStatus: state.chat.escalationStatus,
        conversation: state.chat.conversation,
        conversationAttributes: state.bot.taskAttributes?.conversations as Record<string, unknown> | undefined
    })
);

export function useInQueueMessaging() {
    const { escalationStatus, inQueueMessagingConfig, conversation, conversationAttributes } = useSelector(useInQueueMessagingSelector);
    const dispatch = useDispatch<ThunkDispatch<ChatReducer | SessionReducer, unknown, AnyAction>>();
    const currentMessageIndex = useRef<number>(0);

    const inQueueTimer = useRef<NodeJS.Timeout | null>(null);

    const sendInQueueMessage = useCallback(() => {
        if (!conversation
            || !inQueueMessagingConfig
            || !inQueueMessagingConfig.enabled
            || !inQueueMessagingConfig.messages
            || inQueueMessagingConfig.messages.length < 1
        ) return;

        dispatch(
            sendConversationMessage({
                sentByBot: true,
                body: inQueueMessagingConfig.messages[currentMessageIndex.current],
                conversation,
                conversationAttributes
            })
        );

        // increment the current message index with respect to the length of the messages array
        currentMessageIndex.current = (currentMessageIndex.current + 1) % inQueueMessagingConfig.messages.length;
    }, [inQueueMessagingConfig, dispatch, sendConversationMessage, conversation, conversationAttributes]);

    useEffect(() => {
        if (!inQueueMessagingConfig || !inQueueMessagingConfig.enabled) return;

        if (escalationStatus !== "inQueue") {
            return inQueueTimer.current ? clearInterval(inQueueTimer.current) : undefined;
        }

        sendInQueueMessage();
        inQueueTimer.current = setInterval(() => {
            sendInQueueMessage();
        }, inQueueMessagingConfig.intervalDelayMs ?? 120000); // 2 minutes default interval


        // clear the interval on unmount
        return () => {
            inQueueTimer.current && clearInterval(inQueueTimer.current);
        }
    }, [escalationStatus, sendInQueueMessage, inQueueMessagingConfig])
}